import React from "react";
import { LodgingCardTranslations } from "@bwp/translations/LodgingCardTranslations";
import useGlobalSearchContext from "@bwp/shared/useGlobalSearchContext";
import {getSearchSorting, isValidDate} from "@bwp/shared/helpers";
import { getRandomSeed } from "@bwp/shared/randomSeed";
import { formatISO, parseISO } from "date-fns";
import useQueryString from "@bwp/shared/useQueryString";
import { SearchResultListTranslations } from "@bwp/translations/SearchResultListTranslations";
import { useSearchResultsPageQuery } from "@bwp/operations.generated";
import SearchResultListSkeleton from "@bwp/SearchResultListSkeleton";
import SearchResultList from "@bwp/SearchResultList";
import { lodgingUrl } from "@bwp/shared/lodgingurl";
import { FavouritesTranslations } from "@bwp/translations/FavouritesTranslations";
import {getSearchContextPropsFromBookingOption, SearchContext} from "@bwp/shared/searchcontext";
import {CleaningBannerType, DiscountBannerType, GoogleMapMapType} from "@bwp/shared/types";

export interface SearchResultListWrapperProps {
    mapId: string;
    mapTypeId?: GoogleMapMapType;
    translations: SearchResultListTranslations;
    lodgingCardTranslations: LodgingCardTranslations;
    favouritesTranslations: FavouritesTranslations;
    lodgingUrlPattern: string;
    includeLodgingIdInUrl?: boolean;
    markerIconUrl: string;
    mapHeight: number;
    googleMapsApiKey: string;
    maxMapZoomLevel: number;
    emptyResultHtmlContent: string;
    defaultOrderBy?: string;
    showStars?: boolean;
    starsExtraIcon?: string;
    extraFacilities?: number[];
    headlessMode?: boolean;
    headlessDataFormat?: "full" | "standard" | "simple";
    showProbabilityWithPriceAsBookingOption?: boolean;
    showPriceWithoutMandatoryItems?: boolean;
    showDepositSeparately?: boolean;
    discountBanner?: DiscountBannerType;
    cleaningBanner?: CleaningBannerType;
    hideLodgingAddress?: boolean;
    showItems?: boolean;
    itemsToShow?: number[];
    maxItemsToShow?: number;
    cropImages?: boolean;
    autoSizing?: boolean;
    imageWidth?: number;
    imageHeight?: number;
    durationShortenedByDays?: number;
    durationLengthenedByDays?: number;
}

export default function SearchResultListWrapper({
    mapId,
    mapTypeId,
    translations,
    lodgingCardTranslations,
    favouritesTranslations,
    lodgingUrlPattern,
    includeLodgingIdInUrl,
    markerIconUrl,
    mapHeight,
    googleMapsApiKey,
    maxMapZoomLevel,
    emptyResultHtmlContent,
    defaultOrderBy,
    showStars,
    starsExtraIcon,
    extraFacilities,
    headlessMode,
    headlessDataFormat,
    showProbabilityWithPriceAsBookingOption,
    showPriceWithoutMandatoryItems,
    showDepositSeparately,
    discountBanner,
    cleaningBanner,
    hideLodgingAddress,
    showItems,
    itemsToShow,
    maxItemsToShow,
    cropImages,
    autoSizing,
    imageWidth,
    imageHeight,
    durationShortenedByDays,
    durationLengthenedByDays
}: SearchResultListWrapperProps) {
    const [headerElement] = React.useState(document.getElementById("bwp-search-result-header"));
    const [sorting, setSorting] = useQueryString("ord", null);
    const [searchContext] = useGlobalSearchContext({});
    
    const query = useSearchResultsPageQuery({
        variables: {
            query: searchContext.changeArrivalDensity(3).changeDurationDensity(durationShortenedByDays, durationLengthenedByDays).toServerQuery(),
            onlyMasterFacilities: [],
            onlyFacilities: extraFacilities,
            displayDate: isValidDate(searchContext?.arrival) ? formatISO(searchContext.arrival, {representation: "date"}) : new Date(),
            sorting: (sorting as any) ?? getSearchSorting(defaultOrderBy),
            seed: getRandomSeed(),
        },
        skip: !searchContext.canSearchBookingOptions()
    });
    
    if (query.error) {
        return <div>{query.error}</div>;
    }

    if (query.loading) {
        return <SearchResultListSkeleton headerElement={headerElement} height={405}/>;
    }
    
    const ignoredParams = includeLodgingIdInUrl
        ? []
        : ["lodgingId"];
    
    if (!query.data && !searchContext.canSearchBookingOptions()) {
        return null;
    }

    return (
        <>
            <SearchResultList
                mapId={mapId}
                mapTypeId={mapTypeId}
                headerElement={headerElement}
                translations={translations}
                lodgingCardTranslations={lodgingCardTranslations}
                favouritesTranslations={favouritesTranslations}
                {...query.data}
                lodgingUrlBuilder={(result) => {
                    const baseUrl = lodgingUrl(lodgingUrlPattern, result.lodging);
                    const separator = baseUrl.indexOf('?') === -1 ? "?" : "&";
                    const boProps = getSearchContextPropsFromBookingOption({
                        lodgingId: result.lodging.id,
                        arrival: parseISO(result.selectedBookingOption.arrival),
                        duration: result.selectedBookingOption.duration,
                        adults: result.selectedBookingOption.adults,
                        children: result.selectedBookingOption.children,
                        infants: result.selectedBookingOption.infants,
                        pets: result.selectedBookingOption.pets
                    });
                    const qs = SearchContext.createFromSearchContextProps(boProps).toQueryString(ignoredParams);
                    return `${baseUrl}${separator}${qs}`;
                }}
                extraFacilities={extraFacilities}
                sorting={sorting}
                setSorting={setSorting}
                markerIconUrl={markerIconUrl}
                mapHeight={mapHeight}
                googleMapsApiKey={googleMapsApiKey}
                maxMapZoomLevel={maxMapZoomLevel}
                emptyResultHtmlContent={emptyResultHtmlContent}
                showStars={showStars}
                starsExtraIcon={starsExtraIcon}
                arrivalDate={searchContext.arrival}
                duration={searchContext.duration}
                headlessMode={headlessMode}
                headlessDataFormat={headlessDataFormat}
                showProbabilityWithPriceAsBookingOption={showProbabilityWithPriceAsBookingOption}
                showDepositSeparately={showDepositSeparately}
                showPriceWithoutMandatoryItems={showPriceWithoutMandatoryItems}
                discountBanner={discountBanner}
                cleaningBanner={cleaningBanner}
                hideLodgingAddress={hideLodgingAddress}
                showItems={showItems}
                itemsToShow={itemsToShow}
                maxItemsToShow={maxItemsToShow}
                cropImages={cropImages}
                autoSizing={autoSizing}
                imageWidth={imageWidth}
                imageHeight={imageHeight}
            />
        </>
    );
}
